import React, { useEffect, useState } from "react";
import {
  TrendingUp,
  TrendingDown,
  Users,
  DoorOpen,
  CreditCard,
  Ticket,
  Sparkles,
  ArrowRight,
  ChevronRight,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Zap,
  Bell,
  Building2,
  Home,
  Layers,
  Maximize2,
  UserCheck,
  CalendarDays,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import { useAuth } from "../context/AuthContext";
import { analytics, visitors as visitorsApi, helpdesk, billing, residentBilling, residents as residentsApi, units as unitsApi, type AnalyticsOverview, type AnalyticsTrend, type Visitor, type Ticket as ApiTicket, type Invoice, type ResidentBillingSummary, type Resident, type Unit } from "../services/api";
import type { NexoraUser } from "../context/AuthContext";

function getGreeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

/* ── Types ── */
interface StatCard {
  label: string;
  value: string;
  sub: string;
  trend: "up" | "down" | "neutral";
  trendVal: string;
  color: string;
  icon: React.ReactNode;
}

interface VisitorRow {
  name: string;
  purpose: string;
  unit: string;
  time: string;
  status: "approved" | "pending" | "exited" | "flagged";
}

interface TicketRow {
  id: string;
  issue: string;
  block: string;
  priority: "urgent" | "high" | "medium" | "low";
  status: "open" | "in-progress" | "resolved";
  time: string;
}

/* ── Helpers ── */
const priorityColor = (p: string) =>
  p === "urgent"
    ? { bg: "rgba(239,68,68,0.1)", text: "#DC2626" }
    : p === "high"
      ? { bg: "rgba(245,158,11,0.1)", text: "#D97706" }
      : p === "medium"
        ? { bg: "rgba(26,108,246,0.1)", text: "#1A6CF6" }
        : { bg: "rgba(16,185,129,0.1)", text: "#059669" };

const statusColor = (s: string) =>
  s === "open"
    ? { bg: "rgba(239,68,68,0.1)", text: "#DC2626" }
    : s === "in-progress"
      ? { bg: "rgba(245,158,11,0.1)", text: "#D97706" }
      : { bg: "rgba(16,185,129,0.1)", text: "#059669" };

const timeAgo = (iso: string): string => {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const visitorStatusColor = (s: string) =>
  s === "approved"
    ? { bg: "rgba(16,185,129,0.1)", text: "#059669" }
    : s === "pending"
      ? { bg: "rgba(245,158,11,0.1)", text: "#D97706" }
      : s === "flagged"
        ? { bg: "rgba(239,68,68,0.1)", text: "#DC2626" }
        : { bg: "rgba(148,163,184,0.1)", text: "#64748B" };

/* ── Mini sparkline ── */
const Sparkline: React.FC<{ data: number[]; color: string }> = ({
  data,
  color,
}) => {
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const w = 80;
  const h = 32;
  const pts = data
    .map((v, i) => {
      const x = (i / (data.length - 1)) * w;
      const y = h - ((v - min) / range) * h;
      return `${x},${y}`;
    })
    .join(" ");

  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} fill="none">
      <polyline
        points={pts}
        stroke={color}
        strokeWidth="1.5"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx={w}
        cy={h - ((data[data.length - 1] - min) / range) * h}
        r="3"
        fill={color}
      />
    </svg>
  );
};

/* ── Bar chart ── */
const BarChart: React.FC<{
  data: { label: string; value: number; current?: boolean }[];
}> = ({ data }) => {
  const max = Math.max(...data.map((d) => d.value));
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-end",
        gap: 8,
        height: 100,
        paddingTop: 8,
      }}
    >
      {data.map((d) => (
        <div
          key={d.label}
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 4,
          }}
        >
          <div
            style={{
              fontSize: 9,
              color: d.current ? "var(--nx-blue)" : "var(--nx-text-muted)",
              fontWeight: d.current ? 600 : 400,
            }}
          >
            {d.value >= 100000 ? `₹${(d.value / 100000).toFixed(1)}L` : d.value}
          </div>
          <div
            style={{
              width: "100%",
              borderRadius: "4px 4px 0 0",
              height: `${(d.value / max) * 75}px`,
              background: d.current
                ? "linear-gradient(180deg, #1A6CF6, #3B82F6)"
                : "var(--nx-surface-3)",
              transition: "height 0.6s cubic-bezier(0.34,1.56,0.64,1)",
            }}
          />
          <div
            style={{
              fontSize: 9,
              color: d.current ? "var(--nx-blue)" : "var(--nx-text-muted)",
              fontWeight: d.current ? 600 : 400,
            }}
          >
            {d.label}
          </div>
        </div>
      ))}
    </div>
  );
};

/* ── Resident Dashboard ── */
const ResidentDashboard: React.FC<{ user: NexoraUser; societyId: string }> = ({ user, societyId }) => {
  const [resVisitors, setResVisitors] = useState<Visitor[]>([]);
  const [billingSummary, setBillingSummary] = useState<ResidentBillingSummary | null>(null);
  const [reminderDismissed, setReminderDismissed] = useState(false);
  const [duesReminderDismissed, setDuesReminderDismissed] = useState(false);
  const [myResident, setMyResident] = useState<Resident | null>(null);
  const [myUnit, setMyUnit] = useState<Unit | null>(null);
  const [unitMembers, setUnitMembers] = useState<Resident[]>([]);

  useEffect(() => {
    visitorsApi.list(societyId).then(page => setResVisitors(page.content)).catch(() => {});
    residentBilling.mySummary(societyId).then(setBillingSummary).catch(() => {});
    residentsApi.list(societyId).then(async (page: any) => {
      const allResidents: Resident[] = page.content ?? page;
      const me = allResidents[0] ?? null;
      if (!me) return;
      setMyResident(me);
      setUnitMembers(allResidents.filter((r: Resident) => r.id !== me.id));
      if (me.unitId) {
        unitsApi.get(societyId, me.unitId).then(setMyUnit).catch(() => {});
      }
    }).catch(() => {});
  }, [societyId, user.email]);

  const today = new Date().toDateString();
  const visitorsToday = resVisitors.filter(v => v.entryTime && new Date(v.entryTime).toDateString() === today);
  const pendingVisitors = resVisitors.filter(v => v.status === "pending");
  const approvedVisitors = resVisitors.filter(v => v.status === "approved");

  const residentStats = [
    {
      label: "Visitors Today",
      value: String(visitorsToday.length),
      sub: `${resVisitors.length} total logged`,
      trend: "neutral" as const,
      trendVal: "Today",
      color: "#6D28D9",
      icon: <DoorOpen size={18} />,
    },
    {
      label: "Pending Approvals",
      value: String(pendingVisitors.length),
      sub: pendingVisitors.length > 0 ? "Awaiting entry" : "All clear",
      trend: pendingVisitors.length > 0 ? ("down" as const) : ("up" as const),
      trendVal: pendingVisitors.length > 0 ? "Action needed" : "No pending",
      color: "#F59E0B",
      icon: <Clock size={18} />,
    },
    {
      label: "Approved Entries",
      value: String(approvedVisitors.length),
      sub: "Currently inside",
      trend: "up" as const,
      trendVal: "Active",
      color: "#10B981",
      icon: <CheckCircle2 size={18} />,
    },
    {
      label: "Outstanding Dues",
      value: billingSummary ? `₹${billingSummary.totalOutstanding.toLocaleString("en-IN")}` : "—",
      sub: billingSummary
        ? billingSummary.totalOutstanding === 0
          ? "All paid up!"
          : `₹${billingSummary.currentMonthDue.toLocaleString("en-IN")} this month`
        : "Loading…",
      trend: billingSummary
        ? billingSummary.totalOutstanding === 0 ? ("up" as const) : ("down" as const)
        : ("neutral" as const),
      trendVal: billingSummary
        ? billingSummary.currentMonthStatus === "paid" ? "Paid" : billingSummary.currentMonthStatus === "overdue" ? "Overdue" : "Due"
        : "—",
      color: "#1A6CF6",
      icon: <CreditCard size={18} />,
    },
  ];

  const LockedSection: React.FC<{ title: string; icon: React.ReactNode; message: string }> = ({ title, icon, message }) => (
    <div className="nx-card" style={{ padding: 22, display: "flex", flexDirection: "column" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
        <div>
          <h3 style={{ fontFamily: "Syne, sans-serif", fontSize: 14, fontWeight: 700, color: "var(--nx-text-primary)", marginBottom: 2 }}>{title}</h3>
          <p style={{ fontSize: 11, color: "var(--nx-text-muted)" }}>Managed by society admin</p>
        </div>
      </div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "28px 16px", gap: 12, background: "var(--nx-surface-2)", borderRadius: 10, border: "1px dashed var(--nx-border)" }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: "var(--nx-surface-3)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--nx-text-muted)" }}>
          {icon}
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--nx-text-secondary)", marginBottom: 4 }}>{message}</div>
          <div style={{ fontSize: 11, color: "var(--nx-text-muted)" }}>Your admin will enable this feature for residents</div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div style={{ width: 52, height: 52, borderRadius: 16, background: "var(--nx-grad-brand)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "var(--shadow-blue)", fontFamily: "Syne, sans-serif", fontSize: 20, fontWeight: 700, color: "#fff" }}>
            {user.fullName.charAt(0).toUpperCase()}
          </div>
          <div>
            <h1 style={{ fontFamily: "Syne, sans-serif", fontSize: 24, fontWeight: 800, color: "var(--nx-text-primary)", letterSpacing: "-0.03em", marginBottom: 4 }}>
              {getGreeting()}, {user.fullName.split(" ")[0]} 👋
            </h1>
            <p style={{ fontSize: 13, color: "var(--nx-text-muted)", display: "flex", alignItems: "center", gap: 6 }}>
              <span className="nx-live-dot" /> {user.societyName ?? "Your Society"} · {new Date().toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric", year: "numeric" })} · Here's your personal overview
            </p>
          </div>
        </div>
        <a href="/dashboard/visitors" className="nx-btn nx-btn-primary nx-btn-sm" style={{ gap: 5, textDecoration: "none" }}>
          <DoorOpen size={13} /> Add Visitor
        </a>
      </div>

      {/* My Unit Info Card */}
      {myUnit && (
        <div className="nx-card nx-anim-fade-up" style={{ padding: "18px 22px", marginBottom: 20, display: "flex", flexWrap: "wrap", gap: 24, alignItems: "center" }}>
          {/* Unit identity */}
          <div style={{ display: "flex", alignItems: "center", gap: 14, flex: "0 0 auto" }}>
            <div style={{ width: 48, height: 48, borderRadius: 13, background: "linear-gradient(135deg, rgba(26,108,246,0.15), rgba(109,40,217,0.12))", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Home size={20} style={{ color: "var(--nx-blue)" }} />
            </div>
            <div>
              <div style={{ fontFamily: "Syne, sans-serif", fontSize: 20, fontWeight: 700, color: "var(--nx-text-primary)", letterSpacing: "-0.03em", lineHeight: 1 }}>
                Unit {myUnit.unitNumber}
              </div>
              <div style={{ fontSize: 12, color: "var(--nx-text-muted)", marginTop: 3 }}>
                {myUnit.blockName ?? "Block —"}
              </div>
            </div>
          </div>

          <div style={{ width: 1, height: 40, background: "var(--nx-border)", flexShrink: 0 }} />

          {/* Unit details */}
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
            {[
              { icon: <Building2 size={13} />, label: "Block", value: myUnit.blockName ?? "—" },
              { icon: <Layers size={13} />, label: "Floor", value: myUnit.floor != null ? `Floor ${myUnit.floor}` : "—" },
              { icon: <Maximize2 size={13} />, label: "Area", value: myUnit.areaSqft ? `${myUnit.areaSqft.toLocaleString("en-IN")} sq ft` : "—" },
              { icon: <UserCheck size={13} />, label: "Resident Type", value: myResident?.type ? myResident.type.charAt(0).toUpperCase() + myResident.type.slice(1) : "—" },
              { icon: <CalendarDays size={13} />, label: "Move-in", value: myResident?.moveInDate ? new Date(myResident.moveInDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—" },
            ].map(item => (
              <div key={item.label} style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, fontWeight: 600, color: "var(--nx-text-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  {item.icon} {item.label}
                </div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--nx-text-primary)" }}>{item.value}</div>
              </div>
            ))}
          </div>

          {/* Fellow members */}
          {unitMembers.length > 0 && (
            <>
              <div style={{ width: 1, height: 40, background: "var(--nx-border)", flexShrink: 0 }} />
              <div>
                <div style={{ fontSize: 10, fontWeight: 600, color: "var(--nx-text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 7 }}>
                  Also in this unit
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {unitMembers.map(m => (
                    <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 99, background: "var(--nx-surface-3)", border: "1px solid var(--nx-border)" }}>
                      <div style={{ width: 20, height: 20, borderRadius: "50%", background: "linear-gradient(135deg, rgba(26,108,246,0.2), rgba(109,40,217,0.2))", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: "var(--nx-blue)", flexShrink: 0 }}>
                        {m.fullName[0].toUpperCase()}
                      </div>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 500, color: "var(--nx-text-primary)" }}>{m.fullName}</div>
                        <div style={{ fontSize: 10, color: "var(--nx-text-muted)" }}>{m.type.charAt(0).toUpperCase() + m.type.slice(1)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Pending visitors banner */}
      {!reminderDismissed && pendingVisitors.length > 0 && (
        <div style={{ background: "linear-gradient(135deg, rgba(245,158,11,0.08), rgba(239,68,68,0.05))", border: "1px solid rgba(245,158,11,0.25)", borderRadius: 12, padding: "14px 18px", display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
          <div style={{ width: 36, height: 36, borderRadius: 9, background: "rgba(245,158,11,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <AlertTriangle size={16} style={{ color: "#D97706" }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--nx-text-primary)", marginBottom: 2 }}>Visitor Approval Pending</div>
            <div style={{ fontSize: 12, color: "var(--nx-text-secondary)" }}>
              <strong>{pendingVisitors.length}</strong> visitor{pendingVisitors.length !== 1 ? "s" : ""} awaiting entry approval at your unit.
            </div>
          </div>
          <button onClick={() => setReminderDismissed(true)} style={{ width: 28, height: 28, border: "none", background: "none", cursor: "pointer", color: "var(--nx-text-muted)", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 6 }}>✕</button>
        </div>
      )}

      {/* Maintenance due banner */}
      {!duesReminderDismissed && billingSummary && billingSummary.currentMonthStatus !== "paid" && billingSummary.currentMonthDue > 0 && (
        <div style={{ background: "linear-gradient(135deg, rgba(239,68,68,0.08), rgba(245,158,11,0.05))", border: "1px solid rgba(239,68,68,0.22)", borderRadius: 12, padding: "14px 18px", display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
          <div style={{ width: 36, height: 36, borderRadius: 9, background: "rgba(239,68,68,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <CreditCard size={16} style={{ color: "#DC2626" }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--nx-text-primary)", marginBottom: 2 }}>
              {billingSummary.currentMonthStatus === "overdue" ? "Maintenance Fee Overdue" : "Maintenance Fee Due"}
            </div>
            <div style={{ fontSize: 12, color: "var(--nx-text-secondary)" }}>
              ₹{billingSummary.currentMonthDue.toLocaleString("en-IN")} for this month is {billingSummary.currentMonthStatus}.
            </div>
          </div>
          <a href="/dashboard/my-billing" className="nx-btn nx-btn-primary nx-btn-sm" style={{ gap: 5, textDecoration: "none", flexShrink: 0 }}>
            Pay Now
          </a>
          <button onClick={() => setDuesReminderDismissed(true)} style={{ width: 28, height: 28, border: "none", background: "none", cursor: "pointer", color: "var(--nx-text-muted)", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 6, flexShrink: 0 }}>✕</button>
        </div>
      )}

      {/* Stat cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16, marginBottom: 20 }}>
        {residentStats.map((s, i) => (
          <div
            key={s.label}
            className="nx-anim-fade-up"
            style={{
              padding: "20px",
              animationDelay: `${i * 60}ms`,
              borderRadius: "var(--r-xl)",
              border: `1px solid ${s.color}22`,
              background: `linear-gradient(160deg, var(--nx-surface-1) 0%, ${s.color}0A 100%)`,
              boxShadow: `0 10px 30px ${s.color}14, var(--shadow-sm)`,
              overflow: "hidden",
              position: "relative",
              transition: "transform var(--t-base), box-shadow var(--t-base)",
            }}
          >
            <div style={{ position: "absolute", top: -24, right: -24, width: 90, height: 90, borderRadius: "50%", background: `${s.color}12`, filter: "blur(2px)" }} />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
              <div style={{ width: 40, height: 40, borderRadius: 12, background: `linear-gradient(135deg, ${s.color}2A, ${s.color}12)`, display: "flex", alignItems: "center", justifyContent: "center", color: s.color, boxShadow: `0 4px 14px ${s.color}30` }}>
                {s.icon}
              </div>
              <Sparkline data={[40, 55, 45, 70, 65, 80, s.trend === "up" ? 90 : 60]} color={s.color} />
            </div>
            <div style={{ fontFamily: "Syne, sans-serif", fontSize: 28, fontWeight: 700, color: "var(--nx-text-primary)", letterSpacing: "-0.04em", lineHeight: 1, marginBottom: 4 }}>{s.value}</div>
            <div style={{ fontSize: 11, color: "var(--nx-text-muted)", marginBottom: 8 }}>{s.label}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 500, color: s.trend === "up" ? "var(--nx-green)" : s.trend === "down" ? "var(--nx-red)" : "var(--nx-text-muted)" }}>
              {s.trend === "up" ? <TrendingUp size={12} /> : s.trend === "down" ? <TrendingDown size={12} /> : <Clock size={12} />}
              {s.trendVal} · {s.sub}
            </div>
          </div>
        ))}
      </div>

      {/* Row 2: My Visitors + Quick Actions */}
      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 16, marginBottom: 16 }}>
        {/* My Visitors */}
        <div className="nx-card" style={{ padding: 22 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div>
              <h3 style={{ fontFamily: "Syne, sans-serif", fontSize: 14, fontWeight: 700, color: "var(--nx-text-primary)", marginBottom: 2 }}>My Visitors</h3>
              <p style={{ fontSize: 11, color: "var(--nx-text-muted)" }}>{visitorsToday.length} today · {resVisitors.length} total</p>
            </div>
            <a href="/dashboard/visitors" style={{ fontSize: 11, color: "var(--nx-blue)", textDecoration: "none", display: "flex", alignItems: "center", gap: 3 }}>View all <ArrowRight size={11} /></a>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {resVisitors.length === 0 ? (
              <div style={{ textAlign: "center", padding: "32px 0", color: "var(--nx-text-muted)", fontSize: 13 }}>No visitors logged yet</div>
            ) : resVisitors.slice(0, 6).map(v => {
              const sc = visitorStatusColor(v.status === "denied" ? "flagged" : v.status);
              return (
                <div key={v.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: "1px solid var(--nx-surface-3)" }}>
                  <div style={{ width: 32, height: 32, borderRadius: "50%", flexShrink: 0, background: "linear-gradient(135deg, rgba(26,108,246,0.2), rgba(109,40,217,0.2))", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 600, color: "var(--nx-blue)" }}>
                    {v.name[0]}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: "var(--nx-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{v.name}</div>
                    <div style={{ fontSize: 10, color: "var(--nx-text-muted)" }}>{v.purpose || "—"} · {new Date(v.entryTime ?? "").toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 99, fontSize: 10, fontWeight: 500, background: sc.bg, color: sc.text }}>
                      {v.status.charAt(0).toUpperCase() + v.status.slice(1)}
                    </span>
                    <div style={{ fontSize: 9, color: "var(--nx-text-muted)", marginTop: 2 }}>
                      {new Date(v.entryTime ?? "").toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="nx-card" style={{ padding: 22 }}>
          <div style={{ marginBottom: 18 }}>
            <h3 style={{ fontFamily: "Syne, sans-serif", fontSize: 14, fontWeight: 700, color: "var(--nx-text-primary)", marginBottom: 2 }}>Quick Actions</h3>
            <p style={{ fontSize: 11, color: "var(--nx-text-muted)" }}>Common resident tasks</p>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[
              { label: "Add Visitor", icon: <DoorOpen size={15} />, href: "/dashboard/visitors", color: "#6D28D9", desc: "Pre-approve an expected visitor" },
              { label: "View Notices", icon: <Bell size={15} />, href: "/dashboard/notices", color: "#1A6CF6", desc: "Community announcements" },
              { label: "Events", icon: <Sparkles size={15} />, href: "/dashboard/events", color: "#10B981", desc: "Upcoming community events" },
              { label: "My Deliveries", icon: <Ticket size={15} />, href: "/dashboard/deliveries", color: "#F59E0B", desc: "Track parcel & package status" },
            ].map(action => (
              <a
                key={action.label}
                href={action.href}
                style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 10, border: "1px solid var(--nx-border)", textDecoration: "none", transition: "all 0.15s" }}
                onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.background = `${action.color}08`; (e.currentTarget as HTMLAnchorElement).style.borderColor = `${action.color}40`; }}
                onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.background = "transparent"; (e.currentTarget as HTMLAnchorElement).style.borderColor = "var(--nx-border)"; }}
              >
                <div style={{ width: 36, height: 36, borderRadius: 9, background: `${action.color}15`, display: "flex", alignItems: "center", justifyContent: "center", color: action.color, flexShrink: 0 }}>
                  {action.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--nx-text-primary)" }}>{action.label}</div>
                  <div style={{ fontSize: 11, color: "var(--nx-text-muted)" }}>{action.desc}</div>
                </div>
                <ChevronRight size={14} style={{ color: "var(--nx-text-muted)" }} />
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Row 3: Dues + Tickets */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>

        {/* My Dues & Billing */}
        <div className="nx-card" style={{ padding: 22, display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
            <div>
              <h3 style={{ fontFamily: "Syne, sans-serif", fontSize: 14, fontWeight: 700, color: "var(--nx-text-primary)", marginBottom: 2 }}>My Dues & Billing</h3>
              <p style={{ fontSize: 11, color: "var(--nx-text-muted)" }}>
                {new Date().toLocaleString("en-IN", { month: "long", year: "numeric" })}
              </p>
            </div>
            {billingSummary && (
              <span style={{
                padding: "3px 10px", borderRadius: 99, fontSize: 11, fontWeight: 600,
                background: billingSummary.currentMonthStatus === "paid"
                  ? "rgba(16,185,129,0.1)" : billingSummary.currentMonthStatus === "overdue"
                  ? "rgba(239,68,68,0.1)" : "rgba(245,158,11,0.1)",
                color: billingSummary.currentMonthStatus === "paid"
                  ? "#10B981" : billingSummary.currentMonthStatus === "overdue"
                  ? "#DC2626" : "#D97706",
              }}>
                {billingSummary.currentMonthStatus.charAt(0).toUpperCase() + billingSummary.currentMonthStatus.slice(1)}
              </span>
            )}
          </div>

          {!billingSummary ? (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "28px 16px", gap: 12, background: "var(--nx-surface-2)", borderRadius: 10, border: "1px dashed var(--nx-border)" }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: "var(--nx-surface-3)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--nx-text-muted)" }}>
                <CreditCard size={20} />
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--nx-text-secondary)", marginBottom: 4 }}>No billing data yet</div>
                <div style={{ fontSize: 11, color: "var(--nx-text-muted)" }}>Your admin will set up monthly charges</div>
              </div>
            </div>
          ) : (
            <>
              {/* Outstanding banner */}
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "14px 16px", borderRadius: 10, marginBottom: 14,
                background: billingSummary.totalOutstanding === 0
                  ? "rgba(16,185,129,0.07)" : "rgba(239,68,68,0.06)",
                border: `1px solid ${billingSummary.totalOutstanding === 0 ? "rgba(16,185,129,0.2)" : "rgba(239,68,68,0.18)"}`,
              }}>
                <div>
                  <div style={{ fontSize: 11, color: "var(--nx-text-muted)", marginBottom: 2 }}>Total Outstanding</div>
                  <div style={{ fontFamily: "Syne, sans-serif", fontSize: 24, fontWeight: 700, letterSpacing: "-0.03em", color: billingSummary.totalOutstanding === 0 ? "#10B981" : "#DC2626" }}>
                    ₹{billingSummary.totalOutstanding.toLocaleString("en-IN")}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 11, color: "var(--nx-text-muted)", marginBottom: 2 }}>This Month</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: "var(--nx-text-primary)" }}>
                    ₹{billingSummary.currentMonthDue.toLocaleString("en-IN")}
                  </div>
                </div>
              </div>

              {/* Charge breakdown */}
              {billingSummary.charges.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "var(--nx-text-muted)", letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 4 }}>
                    Charge Breakdown
                  </div>
                  {billingSummary.charges.map((ch, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, padding: "6px 0", borderBottom: "1px solid var(--nx-surface-3)" }}>
                      <span style={{ color: "var(--nx-text-secondary)" }}>{ch.name}</span>
                      <span style={{ fontWeight: 600, color: "var(--nx-text-primary)" }}>₹{ch.amount.toLocaleString("en-IN")}</span>
                    </div>
                  ))}
                </div>
              )}

              {billingSummary.lastPaidAt && (
                <div style={{ marginTop: 10, fontSize: 11, color: "var(--nx-text-muted)", display: "flex", alignItems: "center", gap: 4 }}>
                  <CheckCircle2 size={11} style={{ color: "#10B981" }} />
                  Last paid on {new Date(billingSummary.lastPaidAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                </div>
              )}
            </>
          )}
        </div>

        <LockedSection title="My Tickets" icon={<Ticket size={20} />} message="Ticket history not yet available" />
      </div>
    </>
  );
};

/* ── Dashboard ── */
const DASHBOARD_POLL_MS = 30000;

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const societyId = user?.societyId ?? null;
  const [aiDismissed, setAiDismissed] = useState(false);
  const [duesReminderDismissed, setDuesReminderDismissed] = useState(false);

  // Live data state
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [trends, setTrends] = useState<AnalyticsTrend[]>([]);
  const [liveVisitors, setLiveVisitors] = useState<Visitor[]>([]);
  const [liveTickets, setLiveTickets] = useState<ApiTicket[]>([]);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    if (!societyId || user?.role === 'RESIDENT' || user?.role === 'TENANT') return;
    const sid = societyId;

    const fetchAll = () => Promise.allSettled([
      analytics.overview(sid).then(setOverview),
      analytics.trends(sid).then(setTrends),
      visitorsApi.list(sid).then(page => setLiveVisitors(page.content)),
      helpdesk.list(sid).then(setLiveTickets),
    ]);

    fetchAll().finally(() => setDataLoading(false));
    // Live refresh — keeps the dashboard current without a manual reload.
    const interval = setInterval(fetchAll, DASHBOARD_POLL_MS);
    return () => clearInterval(interval);
  }, [societyId, user?.role]);

  const handleGenerateReport = () => navigate('/dashboard/reports');

  // Build stat cards from live data — no placeholder numbers once loaded.
  const stats: StatCard[] = [
    {
      label: "Total Units",
      value: overview ? overview.totalUnits.toLocaleString() : "—",
      sub: overview ? `${overview.occupiedUnits ?? 0} occupied` : "",
      trend: "up",
      trendVal: "+1.0%",
      color: "#1A6CF6",
      icon: <Users size={18} />,
    },
    {
      label: "Dues Collected",
      value: overview ? `₹${((overview.duesCollected ?? 0) / 100000).toFixed(1)}L` : "—",
      sub: overview ? `${(overview.collectionRate ?? 0).toFixed(0)}% collection rate` : "",
      trend: "up",
      trendVal: "+3.2%",
      color: "#10B981",
      icon: <CreditCard size={18} />,
    },
    {
      label: "Visitors Today",
      value: overview ? String(overview.visitorsToday ?? 0) : "—",
      sub: overview ? `${overview.pendingVisitors ?? 0} pending approval` : "",
      trend: "down",
      trendVal: "-8.1%",
      color: "#6D28D9",
      icon: <DoorOpen size={18} />,
    },
    {
      label: "Open Tickets",
      value: overview ? String(overview.openTickets ?? 0) : "—",
      sub: overview ? `${overview.resolvedToday ?? 0} resolved today` : "",
      trend: "down",
      trendVal: "-12%",
      color: "#F59E0B",
      icon: <Ticket size={18} />,
    },
  ];

  // Map live visitors to VisitorRow shape — empty means no visitors, not fake rows.
  const visitors: VisitorRow[] = liveVisitors.slice(0, 6).map((v) => ({
    name: v.name,
    purpose: v.purpose || "—",
    unit: [v.blockName, v.unitNumber].filter(Boolean).join(" · ") || (v.residentName ?? "—"),
    time: new Date(v.entryTime ?? "").toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    status: v.status === 'denied' ? 'flagged' : v.status as VisitorRow['status'],
  }));

  // Map live tickets to TicketRow shape — empty means no tickets, not fake rows.
  const tickets: TicketRow[] = liveTickets.slice(0, 5).map((t) => ({
    id: `#TK-${t.id.slice(0, 4).toUpperCase()}`,
    issue: t.title,
    block: t.category || "General",
    priority: t.priority as TicketRow['priority'],
    status: t.status === 'closed' ? 'resolved' : t.status as TicketRow['status'],
    time: timeAgo(t.createdAt),
  }));

  // Chart data from trends — empty means no billing history yet.
  const chartData = trends.map((t, i) => ({
    label: t.month,
    value: t.duesCollected,
    current: i === trends.length - 1,
  }));

  const expenseData = [
    { label: "Security", pct: 38, color: "#1A6CF6", value: "₹1.2L" },
    { label: "Electricity", pct: 22, color: "#10B981", value: "₹68K" },
    { label: "Water", pct: 16, color: "#6D28D9", value: "₹52K" },
    { label: "Housekeeping", pct: 12, color: "#F59E0B", value: "₹38K" },
    { label: "Others", pct: 12, color: "#94A3B8", value: "₹38K" },
  ];

  return (
    <div
      style={{
        background: "var(--nx-surface-2)",
        minHeight: "100vh",
        fontFamily: "var(--font-body)",
      }}
    >
      <Navbar variant="dashboard" />
      <Sidebar />

      {/* Main content */}
      <main
        style={{
          marginLeft: 220,
          marginTop: 60,
          padding: "28px 28px 40px",
          minHeight: "calc(100vh - 60px)",
        }}
      >
        {user?.role === "RESIDENT" && societyId ? (
          <ResidentDashboard user={user} societyId={societyId} />
        ) : (<>
        {/* Page header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 24,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ width: 52, height: 52, borderRadius: 16, background: "var(--nx-grad-brand)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: "var(--shadow-blue)", fontFamily: "Syne, sans-serif", fontSize: 20, fontWeight: 700, color: "#fff" }}>
              {(user?.fullName?.charAt(0) || "A").toUpperCase()}
            </div>
            <div>
              <h1
                style={{
                  fontFamily: "Syne, sans-serif",
                  fontSize: 24,
                  fontWeight: 800,
                  color: "var(--nx-text-primary)",
                  letterSpacing: "-0.03em",
                  marginBottom: 4,
                }}
              >
                {getGreeting()}, {user?.fullName?.split(" ")[0] || "Admin"} 👋
              </h1>
              <p style={{ fontSize: 13, color: "var(--nx-text-muted)", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                <span className="nx-live-dot" /> {user?.societyName ?? 'Your Society'}
                {user?.societyCode && (
                  <span style={{ fontSize: 11, fontWeight: 700, color: "var(--nx-blue)", background: "var(--nx-blue-subtle, rgba(59,130,246,0.12))", padding: "2px 8px", borderRadius: 6, letterSpacing: "0.03em" }}>
                    {user.societyCode}
                  </span>
                )}
                · {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })} · Here's your community overview
              </p>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              className="nx-btn nx-btn-primary nx-btn-sm"
              style={{ gap: 5 }}
              onClick={handleGenerateReport}
            >
              <Zap size={13} /> Generate Report
            </button>
          </div>
        </div>

        {/* Maintenance dues reminder banner */}
        {!duesReminderDismissed && overview && overview.pendingDuesCount > 0 && (
          <div style={{ background: "linear-gradient(135deg, rgba(239,68,68,0.08), rgba(245,158,11,0.05))", border: "1px solid rgba(239,68,68,0.22)", borderRadius: "var(--r-lg)", padding: "14px 18px", display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
            <div style={{ width: 36, height: 36, borderRadius: 9, background: "rgba(239,68,68,0.1)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <Bell size={16} style={{ color: "#DC2626" }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: "var(--nx-text-primary)", marginBottom: 2 }}>Maintenance Fees Pending</div>
              <div style={{ fontSize: 12, color: "var(--nx-text-secondary)" }}>
                <strong>{overview.pendingDuesCount}</strong> unit{overview.pendingDuesCount !== 1 ? "s haven't" : " hasn't"} paid this month's maintenance
                — ₹{overview.pendingDuesAmount.toLocaleString("en-IN")} outstanding.
              </div>
            </div>
            <a href="/dashboard/billing" className="nx-btn nx-btn-outline nx-btn-sm" style={{ gap: 5, textDecoration: "none", flexShrink: 0 }}>
              View Billing
            </a>
            <button onClick={() => setDuesReminderDismissed(true)} style={{ width: 28, height: 28, border: "none", background: "none", cursor: "pointer", color: "var(--nx-text-muted)", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 6, flexShrink: 0 }}>✕</button>
          </div>
        )}

        {/* AI Alert Banner */}
        {!aiDismissed && (
          <div
            style={{
              background:
                "linear-gradient(135deg, rgba(26,108,246,0.10), rgba(109,40,217,0.06))",
              border: "1px solid rgba(26,108,246,0.22)",
              borderRadius: "var(--r-lg)",
              padding: "16px 20px",
              display: "flex",
              alignItems: "center",
              gap: 14,
              marginBottom: 24,
              boxShadow: "0 8px 28px rgba(26,108,246,0.10)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div style={{ position: "absolute", inset: 0, background: "var(--nx-grad-glow)", opacity: 0.5, pointerEvents: "none" }} />
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 11,
                background: "var(--nx-grad-brand)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                boxShadow: "var(--shadow-blue)",
                position: "relative",
              }}
            >
              <Sparkles size={18} style={{ color: "#fff" }} />
            </div>
            <div style={{ flex: 1, position: "relative" }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  marginBottom: 3,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
                className="nx-shimmer-text"
              >
                NexaBot AI Insight
              </div>
              <div style={{ fontSize: 12, color: "var(--nx-text-secondary)", lineHeight: 1.5 }}>
                Gate B visitor traffic is <strong>40% above average</strong> for
                Tuesday evenings. 3 unverified entries flagged in last 15 mins.
                Predictive maintenance alert: Lift B requires servicing within{" "}
                <strong>14 days</strong> based on usage patterns.
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexShrink: 0, position: "relative" }}>
              <button
                className="nx-btn nx-btn-primary nx-btn-sm"
                style={{ gap: 5 }}
              >
                Review <ChevronRight size={12} />
              </button>
              <button
                onClick={() => setAiDismissed(true)}
                style={{
                  width: 28,
                  height: 28,
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  color: "var(--nx-text-muted)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 6,
                }}
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* Stat cards */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 16,
            marginBottom: 20,
          }}
        >
          {stats.map((s, i) => (
            <div
              key={s.label}
              className="nx-anim-fade-up"
              style={{
                padding: "20px",
                animationDelay: `${i * 60}ms`,
                borderRadius: "var(--r-xl)",
                border: `1px solid ${s.color}22`,
                background: `linear-gradient(160deg, var(--nx-surface-1) 0%, ${s.color}0A 100%)`,
                boxShadow: `0 10px 30px ${s.color}14, var(--shadow-sm)`,
                overflow: "hidden",
                position: "relative",
                transition: "transform var(--t-base), box-shadow var(--t-base)",
              }}
            >
              {/* Subtle bg tint */}
              <div
                style={{
                  position: "absolute",
                  top: -24,
                  right: -24,
                  width: 90,
                  height: 90,
                  borderRadius: "50%",
                  background: `${s.color}12`,
                  filter: "blur(2px)",
                }}
              />

              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: 12,
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 12,
                    background: `linear-gradient(135deg, ${s.color}2A, ${s.color}12)`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: s.color,
                    boxShadow: `0 4px 14px ${s.color}30`,
                  }}
                >
                  {s.icon}
                </div>
                <Sparkline
                  data={[40, 55, 45, 70, 65, 80, s.trend === "up" ? 90 : 60]}
                  color={s.color}
                />
              </div>

              <div
                style={{
                  fontFamily: "Syne, sans-serif",
                  fontSize: 28,
                  fontWeight: 700,
                  color: "var(--nx-text-primary)",
                  letterSpacing: "-0.04em",
                  lineHeight: 1,
                  marginBottom: 4,
                }}
              >
                {s.value}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: "var(--nx-text-muted)",
                  marginBottom: 8,
                }}
              >
                {s.label}
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  fontSize: 11,
                  fontWeight: 500,
                  color: s.trend === "up" ? "var(--nx-green)" : "var(--nx-red)",
                }}
              >
                {s.trend === "up" ? (
                  <TrendingUp size={12} />
                ) : (
                  <TrendingDown size={12} />
                )}
                {s.trendVal} · {s.sub}
              </div>
            </div>
          ))}
        </div>

        {/* Row 2: Chart + Visitors */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1.4fr 1fr",
            gap: 16,
            marginBottom: 16,
          }}
        >
          {/* Billing chart */}
          <div className="nx-card" style={{ padding: 22, borderRadius: "var(--r-xl)", boxShadow: "var(--shadow-md)" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: 20,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 34, height: 34, borderRadius: 10, background: "linear-gradient(135deg, rgba(16,185,129,0.2), rgba(16,185,129,0.08))", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--nx-green)", flexShrink: 0 }}>
                  <CreditCard size={15} />
                </div>
                <div>
                  <h3
                    style={{
                      fontFamily: "Syne, sans-serif",
                      fontSize: 14,
                      fontWeight: 700,
                      color: "var(--nx-text-primary)",
                      marginBottom: 2,
                    }}
                  >
                    Monthly Dues Collection
                  </h3>
                  <p style={{ fontSize: 11, color: "var(--nx-text-muted)" }}>
                    {trends.length > 0
                      ? `${trends[0].month} — ${trends[trends.length - 1].month}`
                      : "Jul — Dec 2024"}
                  </p>
                </div>
              </div>
              <div style={{ display: "flex", gap: 16, fontSize: 11 }}>
                <div style={{ textAlign: "right" }}>
                  <div style={{ color: "var(--nx-text-muted)" }}>Target</div>
                  <div style={{ fontWeight: 600, color: "var(--nx-text-primary)" }}>
                    {overview
                      ? `₹${((overview.duesTarget ?? 0) / 100000).toFixed(1)}L`
                      : "—"}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ color: "var(--nx-text-muted)" }}>Collected</div>
                  <div style={{ fontWeight: 600, color: "var(--nx-green)" }}>
                    {overview
                      ? `₹${((overview.duesCollected ?? 0) / 100000).toFixed(1)}L`
                      : "—"}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ color: "var(--nx-text-muted)" }}>Pending</div>
                  <div style={{ fontWeight: 600, color: "var(--nx-red)" }}>
                    {overview
                      ? `₹${(((overview.duesTarget ?? 0) - (overview.duesCollected ?? 0)) / 100000).toFixed(1)}L`
                      : "—"}
                  </div>
                </div>
              </div>
            </div>
            {dataLoading ? (
              <div style={{ height: 100, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--nx-text-muted)", fontSize: 12 }}>
                Loading…
              </div>
            ) : chartData.length === 0 ? (
              <div style={{ height: 100, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--nx-text-muted)", fontSize: 12 }}>
                No billing history yet
              </div>
            ) : (
              <BarChart data={chartData} />
            )}
            {/* Progress bar */}
            <div style={{ marginTop: 12 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 11,
                  color: "var(--nx-text-muted)",
                  marginBottom: 6,
                }}
              >
                <span>Collection progress</span>
                <span style={{ fontWeight: 600, color: "var(--nx-text-primary)" }}>
                  {overview
                    ? `${(overview.collectionRate ?? 0).toFixed(1)}%`
                    : "—"}
                </span>
              </div>
              <div
                style={{
                  height: 6,
                  background: "var(--nx-surface-3)",
                  borderRadius: 3,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${overview ? (overview.collectionRate ?? 0) : 0}%`,
                    background: "linear-gradient(90deg, #1A6CF6, #10B981)",
                    borderRadius: 3,
                    animation: "nx-bar-fill 1s cubic-bezier(0.4,0,0.2,1) both",
                  }}
                />
              </div>
            </div>
          </div>

          {/* Visitors */}
          <div className="nx-card" style={{ padding: 22, borderRadius: "var(--r-xl)", boxShadow: "var(--shadow-md)" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 16,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 34, height: 34, borderRadius: 10, background: "linear-gradient(135deg, rgba(109,40,217,0.2), rgba(109,40,217,0.08))", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--nx-violet-mid)", flexShrink: 0 }}>
                  <DoorOpen size={15} />
                </div>
                <h3
                  style={{
                    fontFamily: "Syne, sans-serif",
                    fontSize: 14,
                    fontWeight: 700,
                    color: "var(--nx-text-primary)",
                  }}
                >
                  Visitors Today
                </h3>
              </div>
              <a
                href="#"
                style={{
                  fontSize: 11,
                  color: "var(--nx-blue)",
                  textDecoration: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                }}
              >
                View all <ArrowRight size={11} />
              </a>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {dataLoading ? (
                <div style={{ textAlign: "center", padding: "32px 0", color: "var(--nx-text-muted)", fontSize: 13 }}>Loading…</div>
              ) : visitors.length === 0 ? (
                <div style={{ textAlign: "center", padding: "32px 0", color: "var(--nx-text-muted)", fontSize: 13 }}>No visitors logged yet</div>
              ) : visitors.slice(0, 5).map((v) => {
                const sc = visitorStatusColor(v.status);
                return (
                  <div
                    key={v.name}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "8px 0",
                      borderBottom: "1px solid var(--nx-surface-3)",
                    }}
                  >
                    {/* Avatar */}
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: "50%",
                        flexShrink: 0,
                        background:
                          v.status === "flagged"
                            ? "rgba(239,68,68,0.15)"
                            : "linear-gradient(135deg, rgba(26,108,246,0.2), rgba(109,40,217,0.2))",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 12,
                        fontWeight: 600,
                        color:
                          v.status === "flagged"
                            ? "var(--nx-red)"
                            : "var(--nx-blue)",
                      }}
                    >
                      {v.status === "flagged" ? "⚠" : v.name[0]}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 500,
                          color:
                            v.status === "flagged"
                              ? "var(--nx-red)"
                              : "var(--nx-text-primary)",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {v.name}
                      </div>
                      <div
                        style={{ fontSize: 10, color: "var(--nx-text-muted)" }}
                      >
                        {v.purpose} · {v.unit}
                      </div>
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <span
                        style={{
                          display: "inline-block",
                          padding: "2px 8px",
                          borderRadius: 99,
                          fontSize: 10,
                          fontWeight: 500,
                          background: sc.bg,
                          color: sc.text,
                        }}
                      >
                        {v.status.charAt(0).toUpperCase() + v.status.slice(1)}
                      </span>
                      <div
                        style={{
                          fontSize: 9,
                          color: "var(--nx-text-muted)",
                          marginTop: 2,
                        }}
                      >
                        {v.time}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Row 3: Tickets + Expenses */}
        <div
          style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 16 }}
        >
          {/* Tickets */}
          <div className="nx-card" style={{ padding: 22, borderRadius: "var(--r-xl)", boxShadow: "var(--shadow-md)" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 16,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 34, height: 34, borderRadius: 10, background: "linear-gradient(135deg, rgba(245,158,11,0.2), rgba(245,158,11,0.08))", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--nx-gold)", flexShrink: 0 }}>
                  <Ticket size={15} />
                </div>
                <div>
                  <h3
                    style={{
                      fontFamily: "Syne, sans-serif",
                      fontSize: 14,
                      fontWeight: 700,
                      color: "var(--nx-text-primary)",
                      marginBottom: 2,
                    }}
                  >
                    Helpdesk Tickets
                  </h3>
                  <p style={{ fontSize: 11, color: "var(--nx-text-muted)" }}>
                    {overview ? `${overview.openTickets ?? 0} open · ${overview.resolvedToday ?? 0} resolved today` : "—"}
                  </p>
                </div>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                {["All", "Open", "Urgent"].map((f) => (
                  <button
                    key={f}
                    style={{
                      padding: "4px 10px",
                      borderRadius: 6,
                      fontSize: 11,
                      border:
                        f === "All"
                          ? "1px solid var(--nx-blue)"
                          : "1px solid var(--nx-border)",
                      background:
                        f === "All" ? "rgba(26,108,246,0.08)" : "transparent",
                      color:
                        f === "All" ? "var(--nx-blue)" : "var(--nx-text-muted)",
                      cursor: "pointer",
                    }}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>

            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 12,
              }}
            >
              <thead>
                <tr>
                  {[
                    "ID",
                    "Issue",
                    "Location",
                    "Priority",
                    "Status",
                    "Time",
                  ].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: "left",
                        padding: "6px 8px",
                        fontSize: 10,
                        fontWeight: 600,
                        letterSpacing: "0.06em",
                        color: "var(--nx-text-muted)",
                        borderBottom: "1px solid var(--nx-surface-3)",
                        textTransform: "uppercase",
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dataLoading ? (
                  <tr><td colSpan={6} style={{ padding: "24px 8px", textAlign: "center", color: "var(--nx-text-muted)" }}>Loading…</td></tr>
                ) : tickets.length === 0 ? (
                  <tr><td colSpan={6} style={{ padding: "24px 8px", textAlign: "center", color: "var(--nx-text-muted)" }}>No tickets yet</td></tr>
                ) : tickets.map((t) => {
                  const pc = priorityColor(t.priority);
                  const sc = statusColor(t.status);
                  return (
                    <tr
                      key={t.id}
                      style={{ cursor: "pointer" }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background =
                          "var(--nx-surface-2)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "transparent")
                      }
                    >
                      <td
                        style={{
                          padding: "9px 8px",
                          color: "var(--nx-blue)",
                          fontWeight: 500,
                        }}
                      >
                        {t.id}
                      </td>
                      <td
                        style={{
                          padding: "9px 8px",
                          color: "var(--nx-text-primary)",
                          maxWidth: 160,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t.priority === "urgent" && (
                          <AlertTriangle
                            size={11}
                            style={{
                              color: "var(--nx-red)",
                              marginRight: 4,
                              verticalAlign: "middle",
                            }}
                          />
                        )}
                        {t.issue}
                      </td>
                      <td
                        style={{
                          padding: "9px 8px",
                          color: "var(--nx-text-muted)",
                        }}
                      >
                        {t.block}
                      </td>
                      <td style={{ padding: "9px 8px" }}>
                        <span
                          style={{
                            padding: "2px 8px",
                            borderRadius: 99,
                            fontSize: 10,
                            fontWeight: 500,
                            background: pc.bg,
                            color: pc.text,
                          }}
                        >
                          {t.priority.charAt(0).toUpperCase() +
                            t.priority.slice(1)}
                        </span>
                      </td>
                      <td style={{ padding: "9px 8px" }}>
                        <span
                          style={{
                            padding: "2px 8px",
                            borderRadius: 99,
                            fontSize: 10,
                            fontWeight: 500,
                            background: sc.bg,
                            color: sc.text,
                          }}
                        >
                          {t.status === "in-progress"
                            ? "In Progress"
                            : t.status.charAt(0).toUpperCase() +
                              t.status.slice(1)}
                        </span>
                      </td>
                      <td
                        style={{
                          padding: "9px 8px",
                          color: "var(--nx-text-muted)",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 3,
                          }}
                        >
                          <Clock size={10} /> {t.time}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Expense breakdown */}
          <div className="nx-card" style={{ padding: 22, borderRadius: "var(--r-xl)", boxShadow: "var(--shadow-md)" }}>
            <div style={{ marginBottom: 18, display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: "linear-gradient(135deg, rgba(26,108,246,0.2), rgba(26,108,246,0.08))", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--nx-blue)", flexShrink: 0 }}>
                <Layers size={15} />
              </div>
              <div>
                <h3
                  style={{
                    fontFamily: "Syne, sans-serif",
                    fontSize: 14,
                    fontWeight: 700,
                    color: "var(--nx-text-primary)",
                    marginBottom: 2,
                  }}
                >
                  Expense Breakdown
                </h3>
                <p style={{ fontSize: 11, color: "var(--nx-text-muted)" }}>
                  December 2024 · Total ₹3.16L
                </p>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {expenseData.map((e) => (
                <div key={e.label}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      fontSize: 12,
                      marginBottom: 5,
                    }}
                  >
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 7 }}
                    >
                      <div
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: 2,
                          background: e.color,
                        }}
                      />
                      <span style={{ color: "var(--nx-text-secondary)" }}>
                        {e.label}
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: 10 }}>
                      <span
                        style={{ color: "var(--nx-text-muted)", fontSize: 11 }}
                      >
                        {e.value}
                      </span>
                      <span
                        style={{
                          fontWeight: 600,
                          color: "var(--nx-text-primary)",
                          width: 30,
                          textAlign: "right",
                        }}
                      >
                        {e.pct}%
                      </span>
                    </div>
                  </div>
                  <div
                    style={{
                      height: 5,
                      background: "var(--nx-surface-3)",
                      borderRadius: 3,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: `${e.pct}%`,
                        background: e.color,
                        borderRadius: 3,
                        animation:
                          "nx-bar-fill 1s cubic-bezier(0.4,0,0.2,1) both",
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* AI prediction */}
            <div
              style={{
                marginTop: 18,
                background:
                  "linear-gradient(135deg, rgba(26,108,246,0.06), rgba(109,40,217,0.04))",
                border: "1px solid rgba(26,108,246,0.12)",
                borderRadius: 10,
                padding: "12px 14px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 10,
                  fontWeight: 700,
                  color: "var(--nx-blue)",
                  marginBottom: 6,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                }}
              >
                <Sparkles size={11} /> AI Prediction
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: "var(--nx-text-secondary)",
                  lineHeight: 1.55,
                }}
              >
                Next month's dues collection forecast:{" "}
                <strong style={{ color: "var(--nx-text-primary)" }}>
                  ₹4.6L
                </strong>{" "}
                with{" "}
                <strong style={{ color: "var(--nx-green)" }}>
                  97% confidence
                </strong>
                . Based on 12-month seasonal pattern analysis.
              </div>
            </div>

            {/* Quick actions */}
            <div
              style={{
                marginTop: 14,
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 8,
              }}
            >
              {[
                {
                  label: "Generate Invoice",
                  icon: <CreditCard size={13} />,
                  primary: true,
                },
                {
                  label: "Send Reminder",
                  icon: <Bell size={13} />,
                  primary: false,
                },
              ].map((a) => (
                <button
                  key={a.label}
                  className={`nx-btn ${a.primary ? "nx-btn-primary" : "nx-btn-outline"} nx-btn-sm`}
                  style={{ justifyContent: "center", fontSize: 11 }}
                >
                  {a.icon} {a.label}
                </button>
              ))}
            </div>
          </div>
        </div>
        </>)}
      </main>
    </div>
  );
};

export default Dashboard;
